import brand from '../../../../config/brand.json';
import { officialPonsUrl } from './pons';

/**
 * Brand strings, read from `config/brand.json` at build time.
 *
 * Same file `apps/web/lib/brand.ts` reads, deliberately: the name, the ticker
 * and the compliance line appear on both deployments and a second copy would
 * drift the first time one of them changed. Nothing in this app types the name
 * out — if a string belongs on the door, it belongs in that file.
 *
 * The treasury address is the one field that can differ per deployment, so it
 * is overridable. `apps/web` takes it from `process.env`; a browser bundle has
 * no such thing, so this takes it from `VITE_TREASURY_ADDRESS`, baked in at
 * build time by Vite. Unset means "not published yet", which the pages render
 * as an honest absence rather than a placeholder.
 */

export interface Brand {
  name: string;
  ticker: string;
  handle: string;
  chain: string;
  launchpad: string;
  tagline: string;
  eyebrow: string;
  treasuryAddress: string | null;
  links: { x: string; telegram: string; docs: string; pons: string | null };
  pricing: { protocol: string; perQuery: string };
  tokenRisk: string;
  compliance: string;
}

const override = import.meta.env['VITE_TREASURY_ADDRESS']?.trim();
const raw = brand as Brand;

/*
 * `links.pons` switches the buy chip on. It is only honoured when it is an
 * https token page on ponsfamily.com (see ./pons.ts); anything else — unset,
 * a lookalike domain, the bare homepage — renders the chip disabled.
 */
export const BRAND: Brand = {
  ...raw,
  treasuryAddress: override ? override : (raw.treasuryAddress ?? null),
  links: { ...raw.links, pons: officialPonsUrl(raw.links.pons) },
};

/**
 * The paid x402 endpoint is not deployed (docs/06-api.md). Until it is, every
 * place that quotes the price says so — the desk QA on 28 Sep found the
 * landing describing micropayments that nobody could make. Flip when the API
 * answers a real paid call.
 */
export const API_LIVE = false;

export const priceLabel = `${BRAND.pricing.protocol} · ${BRAND.pricing.perQuery} / query${API_LIVE ? '' : ' · coming soon'}`;
